import React, { useState, useEffect, useContext, useCallback } from "react";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import { useNavigate } from "react-router-dom";
import {
  Fuel, Settings, Clock, CheckCircle2, AlertTriangle,
  Plus, Trash2, Edit2, Save, X, ChevronDown, ChevronRight, ChevronLeft,
  BarChart3, RefreshCw, Zap, Lock, Unlock
} from "lucide-react";
import { AuthContext } from "../App";
import { toast } from "sonner";
import DateInput from "../components/ui/DateInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TURNOS = ["Mañana", "Tarde", "Noche"];
const BOMBAS = Array.from({ length: 8 }, (_, i) => `BOMBA-0${i + 1}`);
const PICOS_COUNT = 6;

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmtNum = (v, d = 2) => v != null ? Number(v).toLocaleString("es-PY", { minimumFractionDigits: d, maximumFractionDigits: d }) : "-";
const fmtGs = (v) => v != null ? `₲ ${Math.round(Number(v)).toLocaleString("es-PY")}` : "-";

// ── TabNav ───────────────────────────────────────────────────────────────────
const TabNav = ({ tabs, active, onChange }) => (
  <div className="flex gap-1 border-b border-white/5 mb-6 overflow-x-auto pb-0">
    {tabs.map(t => (
      <button
        key={t.key}
        onClick={() => onChange(t.key)}
        className={`px-4 py-2.5 text-sm font-body whitespace-nowrap border-b-2 transition-all ${
          active === t.key
            ? "border-cyan-500 text-cyan-400"
            : "border-transparent text-slate-400 hover:text-white"
        }`}
      >
        {t.label}
        {t.badge ? (
          <span className="ml-1.5 bg-amber-500/20 text-amber-400 text-[10px] px-1.5 py-0.5 rounded-full font-mono">
            {t.badge}
          </span>
        ) : null}
      </button>
    ))}
  </div>
);

// ── Badge estado ─────────────────────────────────────────────────────────────
const EstadoBadge = ({ estado }) => (
  <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${
    estado === "Abierto"
      ? "bg-emerald-500/15 text-emerald-400"
      : "bg-slate-500/20 text-slate-400"
  }`}>
    {estado}
  </span>
);

// ════════════════════════════════════════════════════════════════════════════
const LecturaBombasPage = () => {
  const { token, user, hasPermission, activeEmpresaPropia } = useContext(AuthContext);
  const navigate = useNavigate();
  const logo = activeEmpresaPropia?.slug;

  // Permisos
  const canVer        = hasPermission("lectura_bombas.ver");
  const canApertura   = hasPermission("lectura_bombas.apertura_turno");
  const canCierre     = hasPermission("lectura_bombas.cierre_turno");
  const canConfigurar = hasPermission("lectura_bombas.configurar");

  // Tabs disponibles
  const tabs = [
    canVer && { key: "operaciones", label: "⛽ Operaciones" },
    canVer && { key: "historial", label: "Historial" },
    canConfigurar && { key: "configuraciones", label: "⚙️ Configuraciones" },
  ].filter(Boolean);

  const [tab, setTab] = useState(tabs[0]?.key || "operaciones");

  // ── Estado compartido ────────────────────────────────────────────────────
  const [combustibles, setCombustibles] = useState([]);
  const [bombasConfig, setBombasConfig] = useState({});
  const [abiertos, setAbiertos] = useState([]);
  const [loadingData, setLoadingData] = useState(false);

  const qs = logo ? `?logo_tipo=${logo}` : "";

  const fetchBase = useCallback(async () => {
    if (!canVer) return;
    setLoadingData(true);
    try {
      const [resProd, resBombas, resAbiertos] = await Promise.all([
        fetch(`${API}/admin/productos${qs}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/admin/lectura-bombas/bombas${qs}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/admin/lectura-bombas/historial/abiertos${qs}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (resProd.ok) {
        const prods = await resProd.json();
        setCombustibles(
          (prods || [])
            .filter(p => p.activo !== false)
            .map(p => ({ id: p.id, nombre: p.nombre, precio: p.precio_venta || 0, bloqueado: false }))
            .sort((a, b) => a.nombre.localeCompare(b.nombre))
        );
      }
      if (resBombas.ok) setBombasConfig(await resBombas.json());
      if (resAbiertos.ok) setAbiertos(await resAbiertos.json());
    } catch (e) {
      toast.error("Error cargando datos de la pista");
    } finally {
      setLoadingData(false);
    }
  }, [token, logo, canVer]); // eslint-disable-line

  useEffect(() => { fetchBase(); }, [fetchBase]);
  useAutoRefresh(fetchBase, 30_000, true);

  if (!canVer && !canConfigurar) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Fuel className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 font-body">Sin acceso al módulo de Lectura de Bombas</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)}
            className="p-2 text-slate-500 hover:text-white hover:bg-white/5 rounded-lg transition-all">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="w-10 h-10 bg-orange-500/20 rounded-xl flex items-center justify-center">
            <Fuel className="w-5 h-5 text-orange-400" />
          </div>
          <div>
            <h1 className="text-white font-heading text-xl font-bold">Lectura de Bombas</h1>
            <p className="text-slate-500 text-xs font-body">Operación de pista · turnos · combustibles</p>
          </div>
        </div>
        <button onClick={fetchBase} disabled={loadingData}
          className="p-2 text-slate-500 hover:text-white hover:bg-white/5 rounded-lg transition-all">
          <RefreshCw className={`w-4 h-4 ${loadingData ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Alerta turnos abiertos */}
      {abiertos.length > 0 && (
        <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
          <p className="text-amber-300 text-sm font-body">
            {abiertos.length} turno(s) abierto(s) sin cerrar — verificar antes de abrir nuevos.
          </p>
        </div>
      )}

      <TabNav tabs={tabs} active={tab} onChange={setTab} />

      {tab === "operaciones" && (
        <TabOperaciones
          combustibles={combustibles}
          bombasConfig={bombasConfig}
          abiertos={abiertos}
          canApertura={canApertura}
          canCierre={canCierre}
          token={token}
          logo={logo}
          onRefresh={fetchBase}
        />
      )}
      {tab === "historial" && (
        <TabHistorial token={token} logo={logo} bombasConfig={bombasConfig} />
      )}
      {tab === "configuraciones" && (
        <TabConfiguraciones
          combustibles={combustibles}
          bombasConfig={bombasConfig}
          token={token}
          logo={logo}
          onRefresh={fetchBase}
        />
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// TAB OPERACIONES — Apertura y Cierre de turno
// ════════════════════════════════════════════════════════════════════════════
const TabOperaciones = ({ combustibles, bombasConfig, abiertos, canApertura, canCierre, token, logo, onRefresh }) => {
  const [subTab, setSubTab] = useState(canApertura ? "apertura" : "cierre");

  const subTabs = [
    canApertura && { key: "apertura", label: "Apertura de turno" },
    canCierre   && { key: "cierre",   label: "Cierre de turno" },
  ].filter(Boolean);

  return (
    <div>
      <div className="flex gap-2 mb-5">
        {subTabs.map(t => (
          <button key={t.key} onClick={() => setSubTab(t.key)}
            className={`px-4 py-2 rounded-lg text-sm font-body transition-all ${
              subTab === t.key
                ? t.key === "apertura"
                  ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                  : "bg-slate-500/20 text-slate-300 border border-slate-500/30"
                : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"
            }`}>
            {t.key === "apertura" ? <span className="flex items-center gap-1.5"><Unlock className="w-3.5 h-3.5" />{t.label}</span>
                                  : <span className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" />{t.label}</span>}
          </button>
        ))}
      </div>

      {subTab === "apertura" && canApertura && (
        <FormApertura
          combustibles={combustibles}
          bombasConfig={bombasConfig}
          token={token}
          logo={logo}
          onRefresh={onRefresh}
        />
      )}
      {subTab === "cierre" && canCierre && (
        <FormCierre
          abiertos={abiertos}
          token={token}
          logo={logo}
          onRefresh={onRefresh}
        />
      )}
    </div>
  );
};

// ── Formulario de Apertura ───────────────────────────────────────────────────
const FormApertura = ({ combustibles, bombasConfig, token, logo, onRefresh }) => {
  const [turno, setTurno] = useState("Mañana");
  const [bombasSeleccionadas, setBombasSeleccionadas] = useState([]);
  const [lecturas, setLecturas] = useState({});
  const [totalizadores, setTotalizadores] = useState({});
  const [precios, setPrecios] = useState({});
  const [saving, setSaving] = useState(false);
  const [playeroId, setPlayeroId] = useState("");
  const [playeros, setPlayeros] = useState([]);

  const qs = logo ? `?logo_tipo=${logo}` : "";

  useEffect(() => {
    fetch(`${API}/admin/lectura-bombas/playeros${qs}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setPlayeros)
      .catch(() => {});
  }, [token, logo]); // eslint-disable-line

  const toggleBomba = (bid) => {
    if (bombasSeleccionadas.includes(bid)) {
      setBombasSeleccionadas(prev => prev.filter(b => b !== bid));
    } else {
      // Pre-popular precios con los valores del catálogo de combustibles
      const cfg = bombasConfig[bid] || {};
      const newPrices = {};
      (cfg.picos || []).filter(p => p.tipo_combustible).forEach(p => {
        const combData = combustibles.find(c => c.nombre === p.tipo_combustible);
        if (combData?.precio != null) {
          newPrices[`${bid}_${p.nro_pico}`] = String(combData.precio);
        }
      });
      setPrecios(prev => ({ ...prev, ...newPrices }));
      setBombasSeleccionadas(prev => [...prev, bid]);
    }
  };

  const setLectura = (bomba, pico, val) => {
    setLecturas(prev => ({ ...prev, [`${bomba}_${pico}`]: val }));
  };

  const setPrecio = (bomba, pico, val) => {
    setPrecios(prev => ({ ...prev, [`${bomba}_${pico}`]: val }));
  };

  const handleApertura = async () => {
    if (!turno) { toast.error("Seleccioná el turno"); return; }
    if (!playeroId && playeros.length > 0) { toast.error("Seleccioná el playero en turno"); return; }
    if (bombasSeleccionadas.length === 0) { toast.error("Seleccioná al menos una bomba"); return; }
    setSaving(true);
    try {
      const registros = bombasSeleccionadas.map(bid => {
        const cfg = bombasConfig[bid] || {};
        const picos = (cfg.picos || [])
          .filter(p => p.tipo_combustible)
          .map(p => ({
            nro_pico: p.nro_pico,
            tipo_combustible: p.tipo_combustible,
            precio_litro: parseFloat(precios[`${bid}_${p.nro_pico}`] || 0),
            lectura_inicial: parseFloat(lecturas[`${bid}_${p.nro_pico}`] || 0),
          }));
        return {
          bomba_id: bid,
          totalizador_general_inicial: parseFloat(totalizadores[bid] || 0),
          picos,
        };
      });
      const res = await fetch(`${API}/admin/lectura-bombas/apertura${qs}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ turno, registros, playero_id: playeroId || undefined }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || "Error al abrir turno"); return; }
      toast.success(`Turno ${turno} abierto: ${data.registros_creados} bomba(s) registrada(s)`);
      setBombasSeleccionadas([]);
      setLecturas({});
      setTotalizadores({});
      setPrecios({});
      setPlayeroId("");
      onRefresh();
    } catch (e) {
      toast.error("Error al guardar apertura");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Playero en turno */}
      <div className="bg-arandu-dark-light border border-white/5 rounded-xl p-5">
        <label className="text-slate-400 text-xs font-body block mb-2">Playero en turno *</label>
        {playeros.length === 0 ? (
          <p className="text-slate-500 text-xs font-body italic">Sin usuarios disponibles — el turno se registrará a nombre del usuario actual.</p>
        ) : (
          <select
            value={playeroId}
            onChange={e => setPlayeroId(e.target.value)}
            style={{ colorScheme: "dark" }}
            className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-slate-200 text-sm font-body focus:outline-none focus:border-orange-500">
            <option value="">— Seleccionar playero —</option>
            {playeros.map(p => (
              <option key={p.id} value={p.id}>
                {p.name || p.email}{p.role !== "usuario" ? ` (${p.role})` : ""}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Turno */}
      <div className="bg-arandu-dark-light border border-white/5 rounded-xl p-5">
        <label className="text-slate-400 text-xs font-body block mb-2">Turno</label>
        <div className="flex gap-2">
          {TURNOS.map(t => (
            <button key={t} onClick={() => setTurno(t)}
              className={`px-4 py-2 rounded-lg text-sm font-body transition-all ${
                turno === t ? "bg-cyan-500/20 border border-cyan-500/40 text-cyan-300" : "border border-white/10 text-slate-400 hover:text-white"
              }`}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Selección de bombas */}
      <div className="bg-arandu-dark-light border border-white/5 rounded-xl p-5">
        <p className="text-slate-400 text-xs font-body mb-3">Bombas a habilitar para este turno</p>
        {Object.keys(bombasConfig).length === 0 ? (
          <p className="text-slate-500 text-xs font-body italic">Sin bombas configuradas. Configurar en ⚙️ Configuraciones → Configuración de Bombas.</p>
        ) : (
        <div className="grid grid-cols-4 gap-2 mb-5">
          {Object.keys(bombasConfig).sort().map(bid => {
            const cfg = bombasConfig[bid];
            const activos = (cfg?.picos || []).filter(p => p.tipo_combustible).length;
            const sel = bombasSeleccionadas.includes(bid);
            return (
              <button key={bid} onClick={() => toggleBomba(bid)}
                className={`p-3 rounded-xl border text-left transition-all ${
                  sel ? "bg-orange-500/15 border-orange-500/40" : "border-white/10 hover:border-white/20 bg-white/2"
                }`}>
                <div className="flex items-center gap-2 mb-1">
                  <Fuel className={`w-3.5 h-3.5 ${sel ? "text-orange-400" : "text-slate-500"}`} />
                  <span className={`text-xs font-medium font-body ${sel ? "text-orange-300" : "text-slate-300"}`}>
                    {bid.replace("BOMBA-", "Bomba ")}
                  </span>
                </div>
                <span className="text-[10px] text-slate-500">{activos} pico(s) activo(s)</span>
              </button>
            );
          })}
        </div>
        )}

        {/* Lecturas por bomba seleccionada */}
        {bombasSeleccionadas.length > 0 && (
          <div className="space-y-4 border-t border-white/5 pt-4">
            {bombasSeleccionadas.map(bid => {
              const cfg = bombasConfig[bid] || {};
              const picosActivos = (cfg.picos || []).filter(p => p.tipo_combustible);
              return (
                <div key={bid} className="bg-white/2 border border-white/5 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-white text-sm font-medium font-body">{bid}</h4>
                    <div className="flex items-center gap-2">
                      <label className="text-[10px] text-slate-500 font-body">Totalizador general inicial:</label>
                      <input
                        type="number" step="0.01" min="0"
                        value={totalizadores[bid] || ""}
                        onChange={e => setTotalizadores(p => ({ ...p, [bid]: e.target.value }))}
                        placeholder="0.00"
                        className="w-28 bg-arandu-dark border border-white/10 rounded-lg px-2 py-1 text-slate-200 text-xs font-body focus:outline-none focus:border-cyan-500"
                      />
                    </div>
                  </div>
                  {picosActivos.length === 0 ? (
                    <p className="text-slate-500 text-xs font-body italic">Esta bomba no tiene picos configurados. Configurar en ⚙️ Configuraciones.</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      {picosActivos.map(p => {
                        const combData = combustibles.find(c => c.nombre === p.tipo_combustible);
                        const precioDefault = combData?.precio || "";
                        const precioKey = `${bid}_${p.nro_pico}`;
                        return (
                          <div key={p.nro_pico} className="bg-arandu-dark border border-white/5 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs text-slate-300 font-body">
                                Pico {p.nro_pico} — <span className="text-cyan-400">{p.tipo_combustible}</span>
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="text-[10px] text-slate-500 block mb-1">Precio/L</label>
                                <input
                                  type="number" step="0.01" min="0"
                                  value={precios[precioKey] !== undefined ? precios[precioKey] : (precioDefault || "")}
                                  onChange={e => setPrecio(bid, p.nro_pico, e.target.value)}
                                  placeholder={String(precioDefault || "0.00")}
                                  className="w-full bg-arandu-dark border border-white/10 rounded px-2 py-1 text-slate-200 text-xs font-body focus:outline-none focus:border-cyan-500"
                                />
                              </div>
                              <div>
                                <label className="text-[10px] text-slate-500 block mb-1">Lectura inicial</label>
                                <input
                                  type="number" step="0.01" min="0"
                                  value={lecturas[`${bid}_${p.nro_pico}`] || ""}
                                  onChange={e => setLectura(bid, p.nro_pico, e.target.value)}
                                  placeholder="0.00"
                                  className="w-full bg-arandu-dark border border-white/10 rounded px-2 py-1 text-slate-200 text-xs font-body focus:outline-none focus:border-emerald-500"
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <button onClick={handleApertura} disabled={saving || bombasSeleccionadas.length === 0}
        className="w-full py-3 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white rounded-xl font-body text-sm font-medium transition-all flex items-center justify-center gap-2">
        {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Unlock className="w-4 h-4" />}
        {saving ? "Abriendo turno..." : `Abrir turno ${turno}`}
      </button>
    </div>
  );
};

// ── Formulario de Cierre ─────────────────────────────────────────────────────
const FormCierre = ({ abiertos, token, logo, onRefresh }) => {
  const [seleccionado, setSeleccionado] = useState(null);
  const [finales, setFinales] = useState({});
  const [totFinal, setTotFinal] = useState("");
  const [saving, setSaving] = useState(false);

  const qs = logo ? `?logo_tipo=${logo}` : "";

  useEffect(() => {
    setFinales({});
    setTotFinal("");
  }, [seleccionado]);

  const handleCierre = async () => {
    if (!seleccionado) { toast.error("Seleccioná un turno para cerrar"); return; }
    setSaving(true);
    try {
      const picos = seleccionado.picos
        .filter(p => p.tipo_combustible)
        .map(p => ({
          nro_pico: p.nro_pico,
          lectura_final: parseFloat(finales[p.nro_pico] || 0),
        }));
      const res = await fetch(`${API}/admin/lectura-bombas/cierre${qs}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          registro_id: seleccionado.id,
          totalizador_general_final: parseFloat(totFinal || 0),
          picos,
        }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || "Error al cerrar turno"); return; }
      toast.success(`Turno cerrado: ${seleccionado.bomba_id} — ${seleccionado.turno}`);
      setSeleccionado(null);
      onRefresh();
    } catch (e) {
      toast.error("Error al guardar cierre");
    } finally {
      setSaving(false);
    }
  };

  if (abiertos.length === 0) {
    return (
      <div className="bg-arandu-dark-light border border-white/5 rounded-xl p-10 text-center">
        <CheckCircle2 className="w-10 h-10 text-emerald-500/40 mx-auto mb-3" />
        <p className="text-slate-400 font-body text-sm">No hay turnos abiertos para cerrar</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Lista de turnos abiertos */}
      <div className="bg-arandu-dark-light border border-white/5 rounded-xl p-5">
        <p className="text-slate-400 text-xs font-body mb-3">Seleccioná el turno a cerrar</p>
        <div className="space-y-2">
          {abiertos.map(r => (
            <button key={r.id}
              onClick={() => setSeleccionado(prev => prev?.id === r.id ? null : r)}
              className={`w-full p-3 rounded-xl border text-left transition-all ${
                seleccionado?.id === r.id
                  ? "bg-slate-500/20 border-slate-400/40"
                  : "border-white/5 hover:border-white/15 bg-white/2"
              }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Fuel className="w-4 h-4 text-orange-400" />
                  <div>
                    <span className="text-white text-sm font-medium font-body">{r.bomba_id}</span>
                    <span className="text-slate-400 text-xs ml-2 font-body">Turno {r.turno} — {r.fecha}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-500 font-body">{r.playero_nombre}</span>
                  <EstadoBadge estado={r.estado} />
                  <ChevronRight className={`w-4 h-4 text-slate-500 transition-transform ${seleccionado?.id === r.id ? "rotate-90" : ""}`} />
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Formulario de cierre del turno seleccionado */}
      {seleccionado && (
        <div className="bg-arandu-dark-light border border-white/5 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-white font-body font-medium">{seleccionado.bomba_id} — Cierre turno {seleccionado.turno}</h3>
            <div className="flex items-center gap-2">
              <label className="text-[10px] text-slate-500 font-body">Totalizador general final:</label>
              <input
                type="number" step="0.01" min="0"
                value={totFinal}
                onChange={e => setTotFinal(e.target.value)}
                placeholder="0.00"
                className="w-28 bg-arandu-dark border border-white/10 rounded-lg px-2 py-1 text-slate-200 text-xs font-body focus:outline-none focus:border-cyan-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {seleccionado.picos.filter(p => p.tipo_combustible).map(p => (
              <div key={p.nro_pico} className="bg-arandu-dark border border-white/5 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-slate-300 font-body">
                    Pico {p.nro_pico} — <span className="text-cyan-400">{p.tipo_combustible}</span>
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] text-slate-500 block mb-1">Lectura inicial</label>
                    <div className="w-full bg-arandu-dark/50 border border-white/5 rounded px-2 py-1 text-slate-400 text-xs font-mono">
                      {fmtNum(p.lectura_inicial)}
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500 block mb-1">Lectura final *</label>
                    <input
                      type="number" step="0.01" min={p.lectura_inicial}
                      value={finales[p.nro_pico] || ""}
                      onChange={e => setFinales(prev => ({ ...prev, [p.nro_pico]: e.target.value }))}
                      placeholder="0.00"
                      className="w-full bg-arandu-dark border border-white/10 rounded px-2 py-1 text-slate-200 text-xs font-body focus:outline-none focus:border-orange-500"
                    />
                  </div>
                </div>
                {finales[p.nro_pico] && parseFloat(finales[p.nro_pico]) >= parseFloat(p.lectura_inicial) && (
                  <div className="mt-2 text-[10px] text-emerald-400 font-mono">
                    Total: {fmtNum(parseFloat(finales[p.nro_pico]) - parseFloat(p.lectura_inicial))} L
                    {p.precio_litro > 0 && ` · ${fmtGs((parseFloat(finales[p.nro_pico]) - parseFloat(p.lectura_inicial)) * p.precio_litro)}`}
                  </div>
                )}
                {finales[p.nro_pico] && parseFloat(finales[p.nro_pico]) < parseFloat(p.lectura_inicial) && (
                  <div className="mt-2 text-[10px] text-red-400 font-body flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Final menor que inicial
                  </div>
                )}
              </div>
            ))}
          </div>

          <button onClick={handleCierre} disabled={saving}
            className="w-full py-3 bg-slate-600 hover:bg-slate-500 disabled:opacity-40 text-white rounded-xl font-body text-sm font-medium transition-all flex items-center justify-center gap-2">
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            {saving ? "Cerrando turno..." : "Confirmar cierre de turno"}
          </button>
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// TAB HISTORIAL
// ════════════════════════════════════════════════════════════════════════════
const TabHistorial = ({ token, logo, bombasConfig = {} }) => {
  const { hasPermission } = useContext(AuthContext);
  const canEliminar = hasPermission?.("lectura_bombas.eliminar");
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(false);
  const hoyLB = () => new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10);
  const [desde, setDesde] = useState(hoyLB);
  const [hasta, setHasta] = useState(hoyLB);
  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroBomba, setFiltroBomba] = useState("");
  const [filtroPlayero, setFiltroPlayero] = useState("");
  const [playeros, setPlayeros] = useState([]);
  const [expandido, setExpandido] = useState(null);

  const qs = logo ? `?logo_tipo=${logo}` : "?";

  useEffect(() => {
    const params = new URLSearchParams();
    if (logo) params.set("logo_tipo", logo);
    fetch(`${API}/admin/lectura-bombas/playeros?${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setPlayeros)
      .catch(() => {});
  }, [token, logo]); // eslint-disable-line

  const handleEliminar = async (r) => {
    if (!window.confirm(`¿Eliminar registro de ${r.bomba_id} del ${r.fecha} (${r.turno})?`)) return;
    try {
      const params = new URLSearchParams();
      if (logo) params.set("logo_tipo", logo);
      const res = await fetch(`${API}/admin/lectura-bombas/registros/${r.id}?${params}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok || res.status === 204) {
        setRegistros(prev => prev.filter(x => x.id !== r.id));
        toast.success("Registro eliminado");
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || "Error al eliminar");
      }
    } catch { toast.error("Error de conexión"); }
  };

  const buscar = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (logo) params.set("logo_tipo", logo);
      params.set("fecha_desde", desde);
      params.set("fecha_hasta", hasta);
      if (filtroEstado) params.set("estado", filtroEstado);
      if (filtroBomba) params.set("bomba_id", filtroBomba);
      if (filtroPlayero) params.set("playero_id", filtroPlayero);
      const res = await fetch(`${API}/admin/lectura-bombas/historial?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setRegistros(await res.json());
    } catch { toast.error("Error cargando historial"); }
    finally { setLoading(false); }
  };

  useEffect(() => { buscar(); }, []); // eslint-disable-line

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <div className="bg-arandu-dark-light border border-white/5 rounded-xl p-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <div>
            <label className="text-slate-500 text-[10px] font-body block mb-1">Desde</label>
            <DateInput value={desde} onChange={e => setDesde(e.target.value)}
              style={{ colorScheme: "dark" }}
              className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-1.5 text-slate-300 text-sm font-body focus:outline-none focus:border-cyan-500" />
          </div>
          <div>
            <label className="text-slate-500 text-[10px] font-body block mb-1">Hasta</label>
            <DateInput value={hasta} onChange={e => setHasta(e.target.value)}
              style={{ colorScheme: "dark" }}
              className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-1.5 text-slate-300 text-sm font-body focus:outline-none focus:border-cyan-500" />
          </div>
          <div>
            <label className="text-slate-500 text-[10px] font-body block mb-1">Estado</label>
            <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}
              style={{ colorScheme: "dark" }}
              className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-1.5 text-slate-300 text-sm font-body focus:outline-none focus:border-cyan-500">
              <option value="">Todos</option>
              <option value="Abierto">Abierto</option>
              <option value="Cerrado">Cerrado</option>
            </select>
          </div>
          <div>
            <label className="text-slate-500 text-[10px] font-body block mb-1">Bomba</label>
            <select value={filtroBomba} onChange={e => setFiltroBomba(e.target.value)}
              style={{ colorScheme: "dark" }}
              className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-1.5 text-slate-300 text-sm font-body focus:outline-none focus:border-cyan-500">
              <option value="">Todas</option>
              {Object.keys(bombasConfig).sort().map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <label className="text-slate-500 text-[10px] font-body block mb-1">Playero</label>
            <select value={filtroPlayero} onChange={e => setFiltroPlayero(e.target.value)}
              style={{ colorScheme: "dark" }}
              className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-1.5 text-slate-300 text-sm font-body focus:outline-none focus:border-cyan-500">
              <option value="">Todos</option>
              {playeros.map(p => (
                <option key={p.id} value={p.id}>{p.name || p.email}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex gap-2 mt-3 flex-wrap">
          <button
            onClick={() => { const h = hoyLB(); setDesde(h); setHasta(h); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-body font-medium border transition-all ${desde === hoyLB() && hasta === hoyLB() ? "bg-emerald-600 border-emerald-600 text-white" : "border-white/10 text-slate-400 hover:text-white bg-white/5"}`}
          >
            Hoy
          </button>
          <button onClick={buscar} disabled={loading}
            className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white rounded-lg text-sm font-body transition-all flex items-center gap-2">
            {loading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <BarChart3 className="w-3.5 h-3.5" />}
            Buscar
          </button>
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-arandu-dark-light border border-white/5 rounded-xl overflow-hidden">
        {registros.length === 0 ? (
          <div className="py-12 text-center">
            <Clock className="w-8 h-8 text-slate-600 mx-auto mb-2" />
            <p className="text-slate-500 text-sm font-body">Sin registros en el período seleccionado</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-white/5">
              <tr>
                {["Fecha", "Bomba", "Turno", "Playero", "Estado", "Picos activos", ""].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-[11px] text-slate-500 font-medium font-body">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {registros.map((r, idx) => (
                <React.Fragment key={r.id}>
                  <tr className={`border-b border-white/3 hover:bg-white/2 transition-colors ${idx % 2 === 0 ? "" : "bg-white/1"}`}>
                    <td className="px-4 py-2.5 text-slate-300 font-body text-xs">{r.fecha}</td>
                    <td className="px-4 py-2.5 text-white font-medium font-body text-xs">{r.bomba_id}</td>
                    <td className="px-4 py-2.5 text-slate-400 font-body text-xs">{r.turno}</td>
                    <td className="px-4 py-2.5 text-slate-400 font-body text-xs">{r.playero_nombre || "-"}</td>
                    <td className="px-4 py-2.5"><EstadoBadge estado={r.estado} /></td>
                    <td className="px-4 py-2.5 text-slate-500 font-mono text-xs">
                      {(r.picos || []).filter(p => p.tipo_combustible).length}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1.5">
                        {canEliminar && (
                          <button
                            onClick={() => handleEliminar(r)}
                            className="text-slate-600 hover:text-red-400 transition-colors"
                            title="Eliminar registro"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                        <button onClick={() => setExpandido(expandido === r.id ? null : r.id)}
                          className="text-slate-500 hover:text-white transition-colors">
                          <ChevronDown className={`w-4 h-4 transition-transform ${expandido === r.id ? "rotate-180" : ""}`} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandido === r.id && (
                    <tr>
                      <td colSpan={7} className="px-4 py-3 bg-white/2">
                        <div className="grid grid-cols-3 gap-2">
                          {(r.picos || []).filter(p => p.tipo_combustible).map(p => (
                            <div key={p.nro_pico} className="bg-arandu-dark border border-white/5 rounded-lg p-2.5">
                              <span className="text-[11px] text-cyan-400 font-body block mb-1">
                                Pico {p.nro_pico} — {p.tipo_combustible}
                              </span>
                              <div className="text-[10px] text-slate-400 font-mono space-y-0.5">
                                <div>Inicial: {fmtNum(p.lectura_inicial)}</div>
                                <div>Final: {fmtNum(p.lectura_final)}</div>
                                {p.total_expedido != null && (
                                  <div className="text-emerald-400">Total: {fmtNum(p.total_expedido)} L · {fmtGs(p.total_expedido * p.precio_litro)}</div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        {r.totalizador_general_final && (
                          <p className="text-[10px] text-slate-500 mt-2 font-body">
                            Totalizador general: {fmtNum(r.totalizador_general_inicial)} → {fmtNum(r.totalizador_general_final)}
                          </p>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// TAB CONFIGURACIONES
// ════════════════════════════════════════════════════════════════════════════
const TabConfiguraciones = ({ combustibles, bombasConfig, token, logo, onRefresh }) => {
  return (
    <div>
      <ConfigBombas
        combustibles={combustibles}
        bombasConfig={bombasConfig}
        token={token}
        logo={logo}
        onRefresh={onRefresh}
      />
    </div>
  );
};

// ── Config Bombas ─────────────────────────────────────────────────────────────
const ConfigBombas = ({ combustibles, bombasConfig, token, logo, onRefresh }) => {
  const [bombas, setBombas] = useState([]); // lista ordenada de bomba_ids activos
  const [config, setConfig] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const ids = Object.keys(bombasConfig).sort();
    setBombas(ids);
    const init = {};
    ids.forEach(bid => {
      const cfg = bombasConfig[bid] || {};
      init[bid] = {
        totalizador_general_inicial: cfg.totalizador_general_inicial || 0,
        picos: Array.from({ length: PICOS_COUNT }, (_, i) => {
          const saved = (cfg.picos || []).find(p => p.nro_pico === i + 1);
          return { nro_pico: i + 1, tipo_combustible: saved?.tipo_combustible || "" };
        }),
      };
    });
    setConfig(init);
  }, [bombasConfig]);

  const agregarBomba = () => {
    // Auto-nombre: busca el próximo BOMBA-XX que no esté en la lista
    let n = 1;
    let newId;
    do {
      newId = `BOMBA-${String(n).padStart(2, "0")}`;
      n++;
    } while (bombas.includes(newId));
    setBombas(prev => [...prev, newId]);
    setConfig(prev => ({
      ...prev,
      [newId]: {
        totalizador_general_inicial: 0,
        picos: Array.from({ length: PICOS_COUNT }, (_, i) => ({
          nro_pico: i + 1, tipo_combustible: "",
        })),
      },
    }));
  };

  const quitarBomba = (bid) => {
    if (!window.confirm(`¿Quitar ${bid} de la configuración? Se eliminará al guardar.`)) return;
    setBombas(prev => prev.filter(b => b !== bid));
    setConfig(prev => { const next = { ...prev }; delete next[bid]; return next; });
  };

  const setPico = (bid, nro, val) => {
    setConfig(prev => ({
      ...prev,
      [bid]: {
        ...prev[bid],
        picos: prev[bid].picos.map(p => p.nro_pico === nro ? { ...p, tipo_combustible: val } : p),
      },
    }));
  };

  const setTot = (bid, val) => {
    setConfig(prev => ({ ...prev, [bid]: { ...prev[bid], totalizador_general_inicial: val } }));
  };

  const handleGuardar = async () => {
    setSaving(true);
    try {
      const qs = logo ? `?logo_tipo=${logo}` : "";
      const res = await fetch(`${API}/admin/lectura-bombas/bombas${qs}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(config),
      });
      if (!res.ok) { const e = await res.json(); toast.error(e.detail || "Error guardando"); return; }
      toast.success("Configuración de bombas guardada");
      onRefresh();
    } catch { toast.error("Error guardando configuración"); }
    finally { setSaving(false); }
  };

  const combustiblesActivos = combustibles.filter(c => !c.bloqueado);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-slate-400 text-xs font-body">
          {bombas.length} bomba(s) — asigná un combustible a cada pico. Los picos sin combustible quedan inactivos.
        </p>
        <button onClick={agregarBomba}
          className="flex items-center gap-2 px-3 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-lg text-sm font-body transition-all">
          <Plus className="w-3.5 h-3.5" /> Agregar bomba
        </button>
      </div>

      {bombas.length === 0 ? (
        <div className="bg-arandu-dark-light border border-white/5 rounded-xl p-10 text-center">
          <Fuel className="w-8 h-8 text-slate-600 mx-auto mb-2" />
          <p className="text-slate-500 text-sm font-body">Sin bombas configuradas — usá "Agregar bomba" para comenzar</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {bombas.map(bid => (
              <div key={bid} className="bg-arandu-dark-light border border-white/5 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Fuel className="w-4 h-4 text-orange-400" />
                    <span className="text-white font-body font-medium text-sm">{bid}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-[10px] text-slate-500 font-body">Totalizador inicial:</label>
                    <input
                      type="number" step="0.01" min="0"
                      value={config[bid]?.totalizador_general_inicial || ""}
                      onChange={e => setTot(bid, e.target.value)}
                      className="w-24 bg-arandu-dark border border-white/10 rounded px-2 py-1 text-slate-200 text-xs font-body focus:outline-none focus:border-cyan-500"
                    />
                    <button onClick={() => quitarBomba(bid)}
                      className="p-1 text-slate-600 hover:text-red-400 transition-colors" title="Quitar bomba">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {(config[bid]?.picos || []).map(p => (
                    <div key={p.nro_pico}>
                      <label className="text-[10px] text-slate-500 font-body block mb-1">
                        Pico {p.nro_pico}
                        {!p.tipo_combustible && <span className="ml-1 text-slate-600">(inactivo)</span>}
                      </label>
                      <select
                        value={p.tipo_combustible || ""}
                        onChange={e => setPico(bid, p.nro_pico, e.target.value)}
                        style={{ colorScheme: "dark" }}
                        className="w-full bg-arandu-dark border border-white/10 rounded px-2 py-1.5 text-slate-300 text-xs font-body focus:outline-none focus:border-cyan-500">
                        <option value="">[Inactivo]</option>
                        {combustiblesActivos.map(c => (
                          <option key={c.id} value={c.nombre}>{c.nombre}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <button onClick={handleGuardar} disabled={saving}
            className="px-6 py-2.5 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 text-white rounded-xl font-body text-sm font-medium transition-all flex items-center gap-2">
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? "Guardando..." : "Guardar configuración"}
          </button>
        </>
      )}
    </div>
  );
};

export default LecturaBombasPage;
